import { useEffect } from 'react';

/**
 * SEO + structured data — hybrid approach for React 19.
 *
 * - <meta>, <link>, <title> render declaratively → React 19 hoists them to <head>.
 * - document.title is also set imperatively via useEffect for guaranteed update.
 * - JSON-LD scripts are managed imperatively because React 19 only hoists
 *   async scripts, not inline ones with dangerouslySetInnerHTML.
 *
 * IMPORTANT: only render after async data has resolved.
 */

const SITE_NAME = 'Jewelry Refined';
const SITE_URL = 'https://jewelryrefined.com';
const DEFAULT_DESC =
  "Your trusted guide to buying pre-owned luxury jewelry from the world's most prestigious houses.";
const DEFAULT_IMAGE =
  'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=1200';

const toStr = (v, fallback = '') => (v == null ? fallback : String(v));

const buildFullTitle = (title) => {
  const safe = toStr(title);
  return safe ? `${safe} | ${SITE_NAME}` : `${SITE_NAME} — Pre-Owned Luxury Jewelry Guide`;
};

export const SEO = ({
  title,
  description,
  image,
  url,
  type = 'website',
  article = null,
  noindex = false,
  keywords,
}) => {
  const fullTitle = buildFullTitle(title);
  const fullUrl = url ? `${SITE_URL}${toStr(url)}` : SITE_URL;
  const metaDescription = toStr(description, DEFAULT_DESC);
  const metaImage = toStr(image, DEFAULT_IMAGE);

  // Imperatively set document.title — guaranteed to update the browser tab.
  useEffect(() => {
    document.title = fullTitle;
  }, [fullTitle]);

  return (
    <>
      <meta name="description" content={metaDescription} />
      <link rel="canonical" href={fullUrl} />

      {keywords ? <meta name="keywords" content={toStr(keywords)} /> : null}
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : null}

      {/* Open Graph */}
      <meta property="og:type" content={toStr(type, 'website')} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {article?.publishedAt ? (
        <meta property="article:published_time" content={toStr(article.publishedAt)} />
      ) : null}
      {article?.author ? (
        <meta property="article:author" content={toStr(article.author)} />
      ) : null}
      {article?.section ? (
        <meta property="article:section" content={toStr(article.section)} />
      ) : null}
    </>
  );
};

const buildSchema = (type, data) => {
  switch (type) {
    case 'website':
      return {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: SITE_NAME,
        url: SITE_URL,
        description: DEFAULT_DESC,
        potentialAction: {
          '@type': 'SearchAction',
          target: `${SITE_URL}/blog?q={search_term_string}`,
          'query-input': 'required name=search_term_string',
        },
      };
    case 'organization':
      return {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: SITE_NAME,
        url: SITE_URL,
        logo: `${SITE_URL}/favicon.ico`,
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'hello@jewelryrefined.com',
          contactType: 'customer service',
        },
      };
    case 'article':
      return {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: toStr(data.title),
        description: toStr(data.description),
        image: toStr(data.image, DEFAULT_IMAGE),
        datePublished: toStr(data.publishedAt),
        dateModified: toStr(data.modifiedAt || data.publishedAt),
        author: {
          '@type': data.authorTitle ? 'Person' : 'Organization',
          name: toStr(data.author, SITE_NAME),
          ...(data.authorTitle ? { jobTitle: toStr(data.authorTitle) } : {}),
          ...(data.authorUrl
            ? { url: `${SITE_URL}${toStr(data.authorUrl)}` }
            : {}),
          ...(Array.isArray(data.authorSameAs) && data.authorSameAs.length
            ? { sameAs: data.authorSameAs }
            : {}),
        },
        ...(data.reviewer
          ? {
              reviewedBy: {
                '@type': 'Person',
                name: toStr(data.reviewer),
                ...(data.reviewerTitle
                  ? { jobTitle: toStr(data.reviewerTitle) }
                  : {}),
                ...(data.reviewerUrl
                  ? { url: `${SITE_URL}${toStr(data.reviewerUrl)}` }
                  : {}),
              },
            }
          : {}),
        ...(Array.isArray(data.citations) && data.citations.length
          ? {
              citation: data.citations.map((c) => ({
                '@type': 'CreativeWork',
                name: toStr(c.text || c),
              })),
            }
          : {}),
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          logo: { '@type': 'ImageObject', url: `${SITE_URL}/favicon.ico` },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': data.url ? `${SITE_URL}${toStr(data.url)}` : SITE_URL,
        },
      };
    case 'person':
      return {
        '@context': 'https://schema.org',
        '@type': 'Person',
        name: toStr(data.name),
        jobTitle: toStr(data.jobTitle),
        description: toStr(data.bio),
        image: toStr(data.image),
        url: data.url ? `${SITE_URL}${toStr(data.url)}` : SITE_URL,
        ...(Array.isArray(data.sameAs) && data.sameAs.length
          ? { sameAs: data.sameAs }
          : {}),
        ...(Array.isArray(data.knowsAbout) && data.knowsAbout.length
          ? { knowsAbout: data.knowsAbout }
          : {}),
        ...(data.alumniOf
          ? { alumniOf: { '@type': 'EducationalOrganization', name: toStr(data.alumniOf) } }
          : {}),
        worksFor: { '@type': 'Organization', name: SITE_NAME, url: SITE_URL },
      };
    case 'faqpage': {
      const items = Array.isArray(data.items) ? data.items : [];
      if (items.length === 0) return null;
      return {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: items.map((qa) => ({
          '@type': 'Question',
          name: toStr(qa.question),
          acceptedAnswer: {
            '@type': 'Answer',
            text: toStr(qa.answer),
          },
        })),
      };
    }
    case 'breadcrumb': {
      const items = Array.isArray(data.items) ? data.items : [];
      return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: toStr(item.name),
          item: `${SITE_URL}${toStr(item.url)}`,
        })),
      };
    }
    case 'brand':
      return {
        '@context': 'https://schema.org',
        '@type': 'Brand',
        name: toStr(data.name),
        description: toStr(data.description),
        logo: toStr(data.image, DEFAULT_IMAGE),
        url: data.url ? `${SITE_URL}${toStr(data.url)}` : SITE_URL,
      };
    case 'product': {
      // Product + Review + AggregateRating in a single schema (used on /investments/<slug>).
      const review = data.review || null;
      const rating = data.rating || null;
      return {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: toStr(data.name),
        description: toStr(data.description),
        image: toStr(data.image, DEFAULT_IMAGE),
        brand: data.brand
          ? { '@type': 'Brand', name: toStr(data.brand) }
          : undefined,
        category: toStr(data.category, 'Pre-Owned Luxury Jewelry'),
        url: data.url ? `${SITE_URL}${toStr(data.url)}` : SITE_URL,
        ...(data.price
          ? {
              offers: {
                '@type': 'Offer',
                price: data.price,
                priceCurrency: toStr(data.priceCurrency, 'USD'),
                availability: 'https://schema.org/InStock',
                url: data.url ? `${SITE_URL}${toStr(data.url)}` : SITE_URL,
              },
            }
          : {}),
        ...(review
          ? {
              review: {
                '@type': 'Review',
                reviewBody: toStr(review.body),
                author: {
                  '@type': 'Organization',
                  name: SITE_NAME,
                },
                reviewRating: {
                  '@type': 'Rating',
                  ratingValue: review.ratingValue ?? 4.5,
                  bestRating: review.bestRating ?? 5,
                  worstRating: review.worstRating ?? 1,
                },
              },
            }
          : {}),
        ...(rating
          ? {
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: rating.ratingValue ?? 4.5,
                bestRating: rating.bestRating ?? 5,
                worstRating: rating.worstRating ?? 1,
                ratingCount: rating.ratingCount ?? 1,
                reviewCount: rating.reviewCount ?? 1,
              },
            }
          : {}),
      };
    }
    case 'definedterm':
      return {
        '@context': 'https://schema.org',
        '@type': 'DefinedTerm',
        name: toStr(data.term),
        description: toStr(data.definition),
        inDefinedTermSet: {
          '@type': 'DefinedTermSet',
          name: 'Jewelry Refined Glossary',
          url: `${SITE_URL}/glossary`,
        },
        url: data.url ? `${SITE_URL}${toStr(data.url)}` : SITE_URL,
      };
    default:
      return null;
  }
};

/**
 * Imperatively injects a JSON-LD <script> into <head> and removes it on unmount
 * or when the data changes. React 19's metadata hoisting does not handle inline
 * scripts, so this is the most reliable approach.
 */
export const SchemaOrg = ({ type, data = {} }) => {
  const schema = buildSchema(type, data);
  const json = schema ? JSON.stringify(schema) : null;

  useEffect(() => {
    if (!json) return undefined;
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-schema', type);
    el.textContent = json;
    document.head.appendChild(el);
    return () => {
      document.head.removeChild(el);
    };
  }, [type, json]);

  return null;
};

export default SEO;
